import { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";
import { IconButton, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { TextSkeleton } from "../ChatUI/TextSkeleton";
import { WaveformVisualizer } from "../WaveformVisualizer";

export const millisecondsToMinutesAndSeconds = (ms: number) => {
	const minutes = Math.floor(ms / 60000);
	const seconds = ((ms % 60000) / 1000).toFixed(0);
	return `${minutes}:${Number(seconds) < 10 ? "0" : ""}${seconds}`;
};

type Props = {
	uri: string;
	totalTimeInMilliseconds: number;
	currentTimeInMilliseconds: number;
	isPlaying: boolean;
	handlePlayPause: () => void;
	isBuffering: boolean;
	isEnded: boolean;
	onSeek: (progress: number) => Promise<void>;
	hidden?: boolean;
	waveformWidth?: number;
	updateWaveformWidth?: (width: number) => void;
	showWaveform?: boolean;
	useReanimated?: boolean;
};

const AudioPlayer = ({
	uri,
	totalTimeInMilliseconds,
	currentTimeInMilliseconds,
	isPlaying,
	handlePlayPause,
	isBuffering,
	isEnded,
	onSeek,
	hidden,
	waveformWidth,
	updateWaveformWidth,
	showWaveform,
	useReanimated = true,
}: Props) => {
	const theme = useAppTheme();

	const formattedCurentTime = useMemo(
		() => millisecondsToMinutesAndSeconds(currentTimeInMilliseconds),
		[currentTimeInMilliseconds],
	);

	const formattedTotalTime = useMemo(
		() => millisecondsToMinutesAndSeconds(totalTimeInMilliseconds),
		[totalTimeInMilliseconds],
	);

	const isLoading = useMemo(
		() => isBuffering || Number.isNaN(totalTimeInMilliseconds),
		[isBuffering, totalTimeInMilliseconds],
	);

	const progress = useMemo(
		() =>
			totalTimeInMilliseconds
				? currentTimeInMilliseconds / totalTimeInMilliseconds
				: 0,
		[currentTimeInMilliseconds, totalTimeInMilliseconds],
	);

	const progressText = useMemo(
		() => `${formattedCurentTime} / ${formattedTotalTime}`,
		[formattedCurentTime, formattedTotalTime],
	);

	const handleSeek = useCallback(
		async (newProgress: number) => {
			await onSeek(newProgress * totalTimeInMilliseconds);
		},
		[totalTimeInMilliseconds, onSeek],
	);

	const icon = useMemo(() => {
		if (isEnded) {
			return "replay";
		}
		if (isPlaying) {
			return "pause";
		}
		return "play";
	}, [isEnded, isPlaying]);

	return (
		<Box
			borderRadius={theme.roundness * 2}
			backgroundColor={theme.colors.secondaryContainer}
			flexDirection="row"
			alignItems="center"
			width="100%"
			padding={12}
			gap={8}
			display={hidden ? "none" : "flex"}
		>
			<IconButton
				icon={icon}
				loading={isLoading}
				size={20}
				onPress={handlePlayPause}
				containerColor={theme.colors.onSecondaryContainer}
				iconColor={theme.colors.secondaryContainer}
				style={styles.iconButton}
			/>
			<WaveformVisualizer
				showWaveform={showWaveform}
				progress={progress}
				isPlaying={isPlaying}
				onSeek={handleSeek}
				uri={uri}
				activeColor={theme.colors.onSecondaryContainer}
				waveformWidth={waveformWidth}
				updateWaveformWidth={updateWaveformWidth}
				useReanimated={useReanimated}
			/>
			{isLoading ? (
				<Box width={68}>
					<TextSkeleton height={16} />
				</Box>
			) : (
				<Text
					variant="labelLarge"
					numberOfLines={1}
					adjustsFontSizeToFit
					style={styles.text}
				>
					{progressText}
				</Text>
			)}
		</Box>
	);
};

const styles = StyleSheet.create({
	activityIndicator: {
		width: 36,
	},
	iconButton: {
		margin: 0,
	},
	text: {
		paddingHorizontal: 0,
		minWidth: 68,
		textAlign: "right",
	},
});

export default AudioPlayer;
